import React, { useEffect } from "react";
import { motion } from "framer-motion";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import BatteryChargingFullRounded from "@mui/icons-material/BatteryChargingFullRounded";
import BluetoothRounded from "@mui/icons-material/BluetoothRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import MonitorHeartRounded from "@mui/icons-material/MonitorHeartRounded";
import SensorsRounded from "@mui/icons-material/SensorsRounded";
import UsbRounded from "@mui/icons-material/UsbRounded";
import WatchRounded from "@mui/icons-material/WatchRounded";
import WaterDropRounded from "@mui/icons-material/WaterDropRounded";

import useTheme from "../../common/hooks/useTheme";
import PrimaryButton from "../../common/ui/PrimaryButton";
import SecondaryButton from "../../common/ui/SecondaryButton";
import productImage from "./assets/pnoe-hrm.png";
import wordmark from "./assets/pnoe-wordmark.png";
import classes from "./HrmPage.module.css";
import { getHrmFallbackDestination } from "./hrmAppLink";

const capabilities = [
  {
    icon: <WatchRounded />,
    title: "Auto-wear detection",
    description: "Ready when the monitor is worn.",
  },
  {
    icon: <MonitorHeartRounded />,
    title: "Metabolic testing",
    description: "Optimized for PNOĒ assessments.",
  },
  {
    icon: <BluetoothRounded />,
    title: "Bluetooth Low Energy",
    description: "Efficient, seamless connectivity.",
  },
  {
    icon: <WaterDropRounded />,
    title: "IP67 protection",
    description: "Water resistant for active use.",
  },
];

const specifications = [
  { icon: <SensorsRounded />, label: "Sensor", value: "Optical PPG" },
  {
    icon: <BatteryChargingFullRounded />,
    label: "Battery life",
    value: "Up to 60 hours",
  },
  { icon: <BluetoothRounded />, label: "Wireless", value: "Bluetooth LE" },
  { icon: <UsbRounded />, label: "Charging", value: "Magnetic USB" },
  { icon: <WaterDropRounded />, label: "Resistance", value: "IP67" },
  { icon: <WatchRounded />, label: "Fit", value: "Lightweight" },
];

const workflow = [
  {
    number: "01",
    title: "Wear",
    description:
      "A lightweight strap and auto-wear detection keep setup effortless.",
  },
  {
    number: "02",
    title: "Connect",
    description:
      "Bluetooth Low Energy links the monitor with the PNOĒ ecosystem.",
  },
  {
    number: "03",
    title: "Assess",
    description:
      "Capture reliable heart-rate data during metabolic and exercise testing.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0 },
};

const HrmPage = () => {
  const theme = useTheme();

  useEffect(() => {
    const destination = getHrmFallbackDestination({
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
    });

    if (destination) {
      window.location.replace(destination);
    }
  }, []);

  const themeVariables = {
    "--hrm-bg": theme.colorTokens.N[10],
    "--hrm-surface": theme.colors_ds.NEUTRAL_BG_MAIN,
    "--hrm-surface-soft": theme.colorTokens.N[30],
    "--hrm-border": theme.colors_ds.NEUTRAL_BORDER_WEAK,
    "--hrm-border-strong": theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    "--hrm-text-strong": theme.colors_ds.NEUTRAL_TEXT_STRONG,
    "--hrm-text": theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    "--hrm-text-weak": theme.colors_ds.NEUTRAL_TEXT_WEAK,
    "--hrm-primary": theme.colorTokens.P[80],
    "--hrm-primary-hover": theme.colorTokens.P[90],
    "--hrm-primary-soft": theme.colorTokens.P[10],
    "--hrm-primary-mid": theme.colorTokens.P[30],
    "--hrm-dark": theme.colorTokens.N[100],
    "--hrm-dark-soft": theme.colorTokens.N[90],
    "--hrm-white": theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    "--hrm-success": theme.colorTokens.S[60],
  } as React.CSSProperties;

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className={classes.page} style={themeVariables}>
      <header className={classes.header}>
        <div className={classes.headerInner}>
          <img className={classes.wordmark} src={wordmark} alt="PNOĒ" />
          <div className={classes.headerProduct}>
            <span className={classes.headerPulse} aria-hidden="true" />
            Heart Rate Monitor
          </div>
        </div>
      </header>

      <section className={classes.hero} aria-labelledby="hrm-title">
        <div className={classes.heroGrid}>
          <motion.div
            className={classes.heroCopy}
            initial="hidden"
            animate="visible"
            variants={reveal}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <div className={classes.eyebrow}>
              <MonitorHeartRounded aria-hidden="true" />
              PNOĒ Heart Rate Monitor
            </div>
            <h1 id="hrm-title">
              Heart rate, designed for <span>metabolic analysis.</span>
            </h1>
            <p className={classes.heroDescription}>
              Reliable heart-rate data for metabolic assessments and exercise
              testing, delivered through a lightweight monitor engineered for
              precise performance.
            </p>
            <div className={classes.heroActions}>
              <PrimaryButton
                label="View specifications"
                icon={<ArrowDownwardRounded fontSize="small" />}
                onClick={() => scrollToSection("hrm-specifications")}
                sx={{ height: "46px", padding: "12px 18px" }}
              />
              <SecondaryButton
                label="How it works"
                onClick={() => scrollToSection("hrm-workflow")}
                sx={{ height: "46px", padding: "12px 18px" }}
              />
            </div>
            <div className={classes.heroAssurances}>
              <span>
                <CheckCircleRounded aria-hidden="true" /> Precise
              </span>
              <span>
                <CheckCircleRounded aria-hidden="true" /> Lightweight
              </span>
              <span>
                <CheckCircleRounded aria-hidden="true" /> PNOĒ compatible
              </span>
            </div>
          </motion.div>

          <motion.div
            className={classes.productStage}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.08, ease: "easeOut" }}
          >
            <div className={classes.stageGrid} aria-hidden="true" />
            <div className={classes.sensorBadge}>
              <SensorsRounded aria-hidden="true" />
              Optical PPG sensor
            </div>
            <div className={classes.batteryBadge}>
              <BatteryChargingFullRounded aria-hidden="true" />
              <span>
                <strong>60 hours</strong>
                battery life
              </span>
            </div>
            <div className={classes.productHalo} aria-hidden="true" />
            <img
              className={classes.productImage}
              src={productImage}
              alt="Black PNOĒ Heart Rate Monitor"
            />
            <div className={classes.stageCaption}>
              <span>01</span>
              <p>Continuous insight without distracting from the assessment.</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className={classes.capabilitySection} aria-label="Key features">
        <div className={classes.capabilityGrid}>
          {capabilities.map((capability, index) => (
            <motion.article
              key={capability.title}
              className={classes.capabilityCard}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={reveal}
              transition={{ duration: 0.45, delay: index * 0.06 }}
            >
              <div className={classes.capabilityIcon}>{capability.icon}</div>
              <div>
                <h2>{capability.title}</h2>
                <p>{capability.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section
        id="hrm-specifications"
        className={classes.precisionSection}
        aria-labelledby="precision-title"
      >
        <motion.div
          className={classes.precisionIntro}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          variants={reveal}
          transition={{ duration: 0.5 }}
        >
          <div className={classes.sectionIndex}>01 / Precision</div>
          <h2 id="precision-title">Designed to make every signal count.</h2>
          <p>
            Built specifically for the PNOĒ workflow, the monitor balances
            dependable sensing, active-use durability, and a comfortable fit.
          </p>
          <div className={classes.signalCard} aria-label="Heart rate signal">
            <div className={classes.signalHeader}>
              <span>Live signal</span>
              <span className={classes.signalStatus}>Connected</span>
            </div>
            <svg
              viewBox="0 0 640 130"
              role="img"
              aria-label="Heart rate waveform"
            >
              <path
                className={classes.signalGridLine}
                d="M0 65 H640 M160 0 V130 M320 0 V130 M480 0 V130"
              />
              <path
                className={classes.signalLine}
                d="M0 69 L75 69 L98 64 L116 69 L151 69 L170 52 L185 96 L204 20 L226 77 L246 69 L322 69 L344 63 L362 69 L397 69 L417 52 L432 97 L452 19 L474 77 L494 69 L568 69 L590 64 L608 69 L640 69"
              />
            </svg>
            <div className={classes.signalMeta}>
              <span>Optical PPG</span>
              <strong>Ready for assessment</strong>
            </div>
          </div>
        </motion.div>

        <div className={classes.specificationPanel}>
          <div className={classes.specificationHeading}>
            <span>Technical specifications</span>
            <p>Purposeful technology, reduced to the essentials.</p>
          </div>
          <div className={classes.specificationGrid}>
            {specifications.map((specification) => (
              <article
                className={classes.specificationItem}
                key={specification.label}
              >
                <div className={classes.specificationIcon}>
                  {specification.icon}
                </div>
                <span>{specification.label}</span>
                <strong>{specification.value}</strong>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="hrm-workflow"
        className={classes.workflowSection}
        aria-labelledby="workflow-title"
      >
        <div className={classes.workflowHeading}>
          <div className={classes.sectionIndex}>02 / PNOĒ ecosystem</div>
          <h2 id="workflow-title">From signal to insight.</h2>
          <p>
            One streamlined path from wearing the monitor to an informed
            assessment.
          </p>
        </div>
        <div className={classes.workflowGrid}>
          {workflow.map((step, index) => (
            <motion.article
              key={step.number}
              className={classes.workflowCard}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.35 }}
              variants={reveal}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <span className={classes.workflowNumber}>{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
              <div className={classes.workflowLine} aria-hidden="true" />
            </motion.article>
          ))}
        </div>

        <div className={classes.compatibilityPanel}>
          <div>
            <span className={classes.compatibilityEyebrow}>
              Compatible with
            </span>
            <h2>Made for the PNOĒ ecosystem.</h2>
            <p>
              Designed to work seamlessly with the PNOĒ App and PNOĒ metabolic
              assessment platform.
            </p>
          </div>
          <div className={classes.compatibilityList}>
            <span>
              <CheckCircleRounded aria-hidden="true" /> PNOĒ App
            </span>
            <span>
              <CheckCircleRounded aria-hidden="true" /> Metabolic assessment
            </span>
          </div>
        </div>
      </section>

      <footer className={classes.footer}>
        <img src={wordmark} alt="PNOĒ" />
        <p>Unlock peak potential.</p>
        <span>Heart Rate Monitor</span>
      </footer>
    </main>
  );
};

export default HrmPage;
